import { useMemo } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { MESSAGES } from './constants';

/*
DATA EXAMPLES (do not delete):
{
  "type": "log",
  "level": "info",
  "timestamp": "2025-10-31T13:29:36.087490",
  "data": {
    "message": "Initial positions",
    "positions": [
      "PositionWeight(ticker='AXON', weight=0.1, dollar_amount=9650.0, reasoning='Kelly+ α=89.14% p=0.74 σ=60.0% → 10.0%', risk_contribution=0.06)",
      "PositionWeight(ticker='TSLA', weight=0.1, dollar_amount=9650.0, reasoning='Kelly+ α=85.39% p=0.74 σ=60.0% → 10.0%', risk_contribution=0.06)",
      "PositionWeight(ticker='SBUX', weight=0.1, dollar_amount=9650.0, reasoning='Kelly+ α=84.27% p=0.74 σ=60.0% → 10.0%', risk_contribution=0.06)",
      "PositionWeight(ticker='NVDA', weight=0.1, dollar_amount=9650.0, reasoning='Kelly+ α=83.90% p=0.74 σ=60.0% → 10.0%', risk_contribution=0.06)",
      "PositionWeight(ticker='AMD', weight=0.1, dollar_amount=9650.0, reasoning='Kelly+ α=82.02% p=0.74 σ=60.0% → 10.0%', risk_contribution=0.06)",
      "PositionWeight(ticker='LULU', weight=0.1, dollar_amount=9650.0, reasoning='Kelly+ α=81.27% p=0.74 σ=60.0% → 10.0%', risk_contribution=0.06)",
      "PositionWeight(ticker='AMZN', weight=0.1, dollar_amount=9650.0, reasoning='Kelly+ α=81.27% p=0.74 σ=60.0% → 10.0%', risk_contribution=0.06)",
      "PositionWeight(ticker='WDAY', weight=0.1, dollar_amount=9650.0, reasoning='Kelly+ α=80.90% p=0.74 σ=60.0% → 10.0%', risk_contribution=0.06)"
    ]
  }
}

{
  "type": "log",
  "level": "info",
  "timestamp": "2025-10-31T13:29:36.112019",
  "data": {
    "message": "Portfolio Rebalancing Summary",
    "Decision date": "2020-01-02 00:00:00",
    "regime": "UNKNOWN",
    "target_positions": 8,
    "actions": {
      "sold": 0,
      "bought": 8,
      "held": 0,
      "total": 8
    },
    "trades": {
      "sold": [],
      "bought": [
        {
          "ticker": "AXON",
          "alpha": "0.9%"
        },
        {
          "ticker": "TSLA",
          "alpha": "0.9%"
        },
        {
          "ticker": "SBUX",
          "alpha": "0.8%"
        },
        {
          "ticker": "NVDA",
          "alpha": "0.8%"
        },
        {
          "ticker": "AMD",
          "alpha": "0.8%"
        },
        {
          "ticker": "LULU",
          "alpha": "0.8%"
        },
        {
          "ticker": "AMZN",
          "alpha": "0.8%"
        },
        {
          "ticker": "WDAY",
          "alpha": "0.8%"
        }
      ],
      "held": []
    }
  }
}
*/

/**
 * Fixed color palette for tickers
 * Once a ticker gets a color, it keeps that color throughout
 */
const COLOR_PALETTE = [
  '#0088FE',
  '#00C49F',
  '#FFBB28',
  '#FF8042',
  '#8884d8',
  '#82ca9d',
  '#ffc658',
  '#ff7300',
  '#8dd1e1',
  '#d084d0',
  '#ff6b9d',
  '#c44569',
  '#40407a',
  '#34ace0',
  '#ff6348',
  '#999999',
  '#666666',
  '#333333',
  '#000000',
  '#cccccc',
];

/**
 * Format date as dd-mm-yyyy
 * @param {Date|string} dateInput - Date object or date string
 * @returns {string} Formatted date string in dd-mm-yyyy format
 */
const formatDate = (dateInput: Date | string): string => {
  const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

interface Trade {
  ticker?: string;
  symbol?: string;
  alpha?: string;
}

interface Trades {
  sold?: Trade[];
  bought?: Trade[];
  held?: Trade[];
}

interface EventData {
  message: string;
  data?: {
    message?: string;
    positions?: string[];
    'Decision date'?: string;
    'Decision Date'?: string;
    timestamp?: string;
    trades?: Trades;
    holdings?: Array<{ symbol?: string; ticker?: string }>;
  };
}

interface PortfolioRebalancingChartProps {
  data?: EventData[];
}

interface ChartDataPoint {
  date: string;
  dateDisplay: string;
  isInitial: boolean;
  [ticker: string]: string | boolean | number | null;
}

interface ChartData {
  data: ChartDataPoint[];
  tickers: string[];
  tickerColors: Record<string, string>;
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{
    name: string;
    value: number | null;
    color: string;
  }>;
  label?: string;
}

/**
 * Custom Tooltip component that sorts by weight (descending) and formats percentages to 2 decimals
 */
const CustomTooltip: React.FC<CustomTooltipProps> = ({
  active,
  payload,
  label,
}) => {
  if (active && payload && payload.length) {
    // Filter out null values and sort by value (weight) in descending order
    const sortedPayload = payload
      .filter((entry) => entry.value !== null && entry.value !== undefined)
      .sort((a, b) => (b.value || 0) - (a.value || 0));

    return (
      <div className="relative z-[5] rounded-md border border-border bg-[color:var(--nq-bg-deep)]/95 p-2.5 opacity-100 shadow-[0_2px_4px_rgba(0,0,0,0.3)] backdrop-blur-[10px]">
        <p className="mb-2 font-bold text-foreground">{label}</p>
        <ul className="m-0 pl-5">
          {sortedPayload.map((entry, index) => (
            <li key={index} className="mb-1" style={{ color: entry.color }}>
              <span className="text-foreground">
                {entry.name}:{' '}
                {typeof entry.value === 'number'
                  ? entry.value.toFixed(2)
                  : entry.value}
                %
              </span>
            </li>
          ))}
        </ul>
      </div>
    );
  }
  return null;
};

/**
 * PortfolioRebalancingChart Component
 *
 * Displays a line chart showing portfolio tickers over time with fixed colors.
 * Each line represents a ticker, tracking initial positions and incremental rebalancing events.
 * Tickers maintain their assigned color throughout the chart.
 *
 * @param {Object} props
 * @param {Array} props.data - Array of relevant events from WebSocket
 */
const PortfolioRebalancingChart: React.FC<PortfolioRebalancingChartProps> = ({
  data = [],
}) => {
  const getTradeTicker = (trade: Trade): string | null => {
    const raw = trade.ticker || trade.symbol;
    return raw ? raw.toUpperCase() : null;
  };

  const getHoldingTicker = (holding: {
    symbol?: string;
    ticker?: string;
  }): string | null => {
    const raw = holding.symbol || holding.ticker;
    return raw ? raw.toUpperCase() : null;
  };

  // Parse PositionWeight string to extract ticker and weight
  const parsePositionWeight = (positionStr: string) => {
    const tickerMatch = positionStr.match(/ticker='([^']+)'/);
    const weightMatch = positionStr.match(/weight=([\d.]+)/);

    return {
      ticker: tickerMatch ? tickerMatch[1].toUpperCase() : null,
      weight: weightMatch ? parseFloat(weightMatch[1]) : null,
    };
  };

  // Transform events into chart data with ticker state management
  const chartData = useMemo((): ChartData => {
    // Find initial positions event
    const initialPositionsEvent = data.find(
      (event) =>
        event.data?.message === MESSAGES.INITIAL_POSITIONS &&
        event.data?.positions
    );

    // Get all rebalancing events, sorted by date
    const rebalancingEvents = data
      .filter(
        (event) =>
          event.data?.message === MESSAGES.PORTFOLIO_REBALANCING_SUMMARY &&
          event.data?.['Decision date']
      )
      .sort((a, b) => {
        const dateA = new Date(a.data!['Decision date']!);
        const dateB = new Date(b.data!['Decision date']!);
        return dateA.getTime() - dateB.getTime();
      });

    // If no data, return empty
    if (!initialPositionsEvent && rebalancingEvents.length === 0) {
      return {
        data: [],
        tickers: [],
        tickerColors: {},
      };
    }

    // Step 1: Collect all unique tickers and assign fixed colors
    const allTickersSet = new Set<string>();
    const tickerColors: Record<string, string> = {};
    const tickerOrder: string[] = [];
    let colorIndex = 0;

    // Collect tickers from initial positions
    if (initialPositionsEvent && initialPositionsEvent.data?.positions) {
      initialPositionsEvent.data.positions.forEach((positionStr) => {
        const parsed = parsePositionWeight(positionStr);
        if (parsed.ticker && !allTickersSet.has(parsed.ticker)) {
          allTickersSet.add(parsed.ticker);
          tickerColors[parsed.ticker] =
            COLOR_PALETTE[colorIndex % COLOR_PALETTE.length];
          tickerOrder.push(parsed.ticker);
          colorIndex++;
        }
      });
    }

    // Collect tickers from rebalancing events
    rebalancingEvents.forEach((event) => {
      const trades = event.data?.trades || {};
      const allTrades = [
        ...(trades.bought || []),
        ...(trades.sold || []),
        ...(trades.held || []),
      ];

      allTrades.forEach((trade) => {
        const ticker = getTradeTicker(trade);
        if (!ticker) return;
        if (!allTickersSet.has(ticker)) {
          allTickersSet.add(ticker);
          tickerColors[ticker] =
            COLOR_PALETTE[colorIndex % COLOR_PALETTE.length];
          tickerOrder.push(ticker);
          colorIndex++;
        }
      });

      // Prefer holdings list (FinIntellect) for the post-rebalance portfolio composition
      const holdings = event.data?.holdings || [];
      holdings.forEach((h) => {
        const ticker = getHoldingTicker(h);
        if (!ticker) return;
        if (!allTickersSet.has(ticker)) {
          allTickersSet.add(ticker);
          tickerColors[ticker] =
            COLOR_PALETTE[colorIndex % COLOR_PALETTE.length];
          tickerOrder.push(ticker);
          colorIndex++;
        }
      });
    });

    // Step 2: Parse initial percentages (differentiate initial vs incremental)
    const initialPercentages: Record<string, number> = {};
    if (initialPositionsEvent && initialPositionsEvent.data?.positions) {
      initialPositionsEvent.data.positions.forEach((positionStr) => {
        const parsed = parsePositionWeight(positionStr);
        if (parsed.ticker && parsed.weight !== null) {
          initialPercentages[parsed.ticker] = parsed.weight * 100; // Convert to percentage
        }
      });
    }

    // Step 3: Build timeline starting with initial positions
    const timeline: ChartDataPoint[] = [];
    let currentPercentages: Record<string, number> = { ...initialPercentages };

    // Add initial point if we have initial positions
    if (Object.keys(initialPercentages).length > 0 && initialPositionsEvent) {
      let initialDate = initialPositionsEvent.data?.timestamp;
      if (!initialDate && rebalancingEvents.length > 0) {
        const firstDate = new Date(
          rebalancingEvents[0].data!['Decision date']!
        );
        firstDate.setDate(firstDate.getDate() - 1);
        initialDate = firstDate.toISOString();
      }
      if (!initialDate) {
        initialDate = new Date().toISOString();
      }

      const initialPoint: ChartDataPoint = {
        date: initialDate,
        dateDisplay: formatDate(initialDate),
        isInitial: true,
      };

      // Add all ticker percentages (initial values)
      tickerOrder.forEach((ticker) => {
        initialPoint[ticker] =
          initialPercentages[ticker] !== undefined
            ? initialPercentages[ticker]
            : null;
      });

      timeline.push(initialPoint);
    } else if (rebalancingEvents.length > 0) {
      // No "Initial positions" event (FinIntellect) — synthesize an initial point
      // from the first rebalance composition so the chart draws from the start.
      const firstEvent = rebalancingEvents[0];
      const firstDateStr =
        firstEvent.data?.['Decision date'] ||
        firstEvent.data?.['Decision Date'] ||
        '';
      const firstDate = new Date(firstDateStr);
      if (!isNaN(firstDate.getTime())) {
        firstDate.setDate(firstDate.getDate() - 1);
      }

      // Determine initial composition from holdings (preferred) or trades (fallback)
      const initialTickers = new Set<string>();
      const holdings = firstEvent.data?.holdings || [];
      if (holdings.length > 0) {
        holdings.forEach((h) => {
          const t = getHoldingTicker(h);
          if (t) initialTickers.add(t);
        });
      } else {
        const trades = firstEvent.data?.trades || {};
        const allTrades = [...(trades.held || []), ...(trades.bought || [])];
        allTrades.forEach((tr) => {
          const t = getTradeTicker(tr);
          if (t) initialTickers.add(t);
        });
      }

      if (initialTickers.size > 0) {
        const equalWeight = 100 / initialTickers.size;
        initialTickers.forEach((t) => {
          currentPercentages[t] = equalWeight;
        });

        const initialPoint: ChartDataPoint = {
          date: !isNaN(firstDate.getTime())
            ? firstDate.toISOString()
            : new Date().toISOString(),
          dateDisplay: formatDate(
            !isNaN(firstDate.getTime())
              ? firstDate.toISOString()
              : new Date().toISOString()
          ),
          isInitial: true,
        };

        tickerOrder.forEach((ticker) => {
          initialPoint[ticker] =
            currentPercentages[ticker] !== undefined
              ? currentPercentages[ticker]
              : null;
        });

        timeline.push(initialPoint);
      }
    }

    // Step 4: Process each rebalancing event (incremental changes)
    rebalancingEvents.forEach((event) => {
      const decisionDate =
        event.data!['Decision date'] || event.data!['Decision Date'] || '';
      const trades = event.data?.trades || {};

      // Collect all tickers in portfolio after this rebalance
      const portfolioTickers = new Set<string>();

      // Prefer holdings list (FinIntellect)
      const holdings = event.data?.holdings || [];
      if (holdings.length > 0) {
        holdings.forEach((h) => {
          const t = getHoldingTicker(h);
          if (t) portfolioTickers.add(t);
        });
      } else {
        if (trades.held) {
          trades.held.forEach((holding) => {
            const t = getTradeTicker(holding);
            if (t) portfolioTickers.add(t);
          });
        }

        if (trades.bought) {
          trades.bought.forEach((buy) => {
            const t = getTradeTicker(buy);
            if (t) portfolioTickers.add(t);
          });
        }
      }

      // Remove sold positions (incremental change: position removed)
      if (trades.sold) {
        trades.sold.forEach((sell) => {
          const ticker = getTradeTicker(sell);
          if (!ticker) return;
          delete currentPercentages[ticker];
          portfolioTickers.delete(ticker);
        });
      }

      // Update percentages based on rebalancing
      // If portfolio composition changed (bought/sold), redistribute equally
      // Otherwise maintain current weights for held positions
      const hasChanges =
        (trades.bought && trades.bought.length > 0) ||
        (trades.sold && trades.sold.length > 0);

      if (hasChanges && portfolioTickers.size > 0) {
        // Portfolio composition changed - redistribute equally
        const equalWeight = 100 / portfolioTickers.size;
        portfolioTickers.forEach((ticker) => {
          currentPercentages[ticker] = equalWeight;
        });
      } else if (portfolioTickers.size > 0) {
        // Only held positions - maintain their current weights
        // Normalize to sum to 100%
        const heldTickersList = Array.from(portfolioTickers);
        const totalWeight = heldTickersList.reduce((sum, ticker) => {
          return sum + (currentPercentages[ticker] || 0);
        }, 0);

        if (totalWeight > 0 && totalWeight !== 100) {
          const scaleFactor = 100 / totalWeight;
          heldTickersList.forEach((ticker) => {
            if (currentPercentages[ticker] !== undefined) {
              currentPercentages[ticker] =
                currentPercentages[ticker] * scaleFactor;
            }
          });
        }
      }

      // Create data point for this rebalancing event (incremental change)
      const dataPoint: ChartDataPoint = {
        date: decisionDate,
        dateDisplay: formatDate(decisionDate),
        isInitial: false,
      };

      // Add current percentage for each ticker
      tickerOrder.forEach((ticker) => {
        dataPoint[ticker] =
          currentPercentages[ticker] !== undefined
            ? currentPercentages[ticker]
            : null;
      });

      timeline.push(dataPoint);
    });

    // Sort timeline by date
    timeline.sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return dateA.getTime() - dateB.getTime();
    });

    return {
      data: timeline,
      tickers: tickerOrder,
      tickerColors,
    };
  }, [data]);

  if (!data || data.length === 0) {
    return (
      <div className="p-5 text-center text-foreground">
        <p>No portfolio rebalancing data available</p>
      </div>
    );
  }

  if (!chartData || !chartData.data || chartData.data.length === 0) {
    return (
      <div>
        <h3 className="mb-5 font-display text-foreground">
          Portfolio Rebalancing Summary
        </h3>
        <div
          className="animate-pulse rounded-md border border-border bg-muted flex h-[300px] items-end px-4 pb-4 pt-6"
          aria-hidden
        >
          <svg
            width="100%"
            height="100%"
            viewBox="0 0 600 260"
            preserveAspectRatio="none"
            aria-hidden
          >
            <polyline
              points="0,180 70,160 140,170 200,140 270,150 340,120 410,130 480,100 560,90 600,80"
              fill="none"
              stroke="rgba(255,255,255,0.10)"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <polyline
              points="0,210 70,200 140,205 200,185 270,190 340,170 410,175 480,155 560,145 600,135"
              fill="none"
              stroke="rgba(255,255,255,0.06)"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <p className="mt-2.5 text-center text-xs text-muted-foreground">
          Populates as rebalancing events arrive
        </p>
      </div>
    );
  }

  return (
    <div>
      <h3 className="mb-5 font-display text-foreground">
        Portfolio Rebalancing Summary
      </h3>
      <ResponsiveContainer width="100%" height={400}>
        <LineChart
          data={chartData.data}
          margin={{
            top: 5,
            right: 30,
            left: 20,
            bottom: 60,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="dateDisplay"
            angle={-45}
            textAnchor="end"
            height={100}
            interval={0}
          />
          <YAxis
            label={{
              value: 'Portfolio Weight (%)',
              angle: -90,
              position: 'insideLeft',
            }}
          />
          <Tooltip
            content={<CustomTooltip />}
            wrapperStyle={{
              pointerEvents: 'none',
              backgroundColor: 'rgba(13, 17, 30, 0.95)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              opacity: 1,
              zIndex: 5,
            }}
            contentStyle={{
              opacity: 1,
              backgroundColor: 'transparent',
            }}
          />
          <Legend />
          {chartData.tickers.map((ticker) => (
            <Line
              key={ticker}
              type="monotone"
              dataKey={ticker}
              stroke={chartData.tickerColors[ticker] || '#888'}
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
              name={ticker}
              connectNulls={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default PortfolioRebalancingChart;
